import type { AICopywriterPostChild, Block } from '$lib/types/AICopywriter/types';
import { tagToBlockTypeMap, isValidTagName } from '$lib/types/AICopywriter/types';

export const parseTextBlockFromPostChild = (block: AICopywriterPostChild) => {
	// If the block does not have a body, return null to indicate that the block is not valid
	if (!block.body) {
		return null;
	}

	const parser = new DOMParser();
	const doc = parser.parseFromString(block.body, 'text/html');
	const firstElement = doc.body.firstElementChild;

	// If there are no elements, return null to indicate that the block is not valid
	if (!firstElement) {
		return null;
	}
	const tagName = firstElement.tagName.toLowerCase();

	// If the tag name is not valid, return null to indicate that the block is not valid
	if (!isValidTagName(tagName)) {
		return null;
	}
	const blockType = tagToBlockTypeMap[tagName];

	switch (blockType) {
		case 'blockquote': {
			// Checks if there is a nested code block inside of the blockquote
			const preElement = firstElement.querySelector('pre');
			if (preElement) {
				const content = preElement.outerHTML;
				return { id: block.id, type: 'blockquote', content } satisfies Block;
			}

			const paragraph = firstElement.querySelector('p');
			const content = paragraph ? paragraph.textContent || '' : firstElement.textContent || '';
			return { id: block.id, type: blockType, content } satisfies Block;
		}
		case 'codeBlock': {
			const code = firstElement.querySelector('code');
			const content = code ? code.textContent || '' : firstElement.textContent || '';
			return { id: block.id, type: blockType, content } satisfies Block;
		}
		case 'orderedList':
		case 'unorderedList': {
			const listItems = Array.from(firstElement.querySelectorAll('li')).map(
				(li) => li.textContent || ''
			);
			return { id: block.id, type: blockType, content: listItems } satisfies Block;
		}
		// Handles titles, subtitles, and paragraphs
		default: {
			return { id: block.id, type: blockType, content: firstElement.innerHTML } satisfies Block;
		}
	}
};
